import { callBackendCommand, firebaseFunctionCommands, getBlobStoreClient } from '@/backend';
import type { BackendCommand, UploadSession, UploadTask } from '@/backend/contracts';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { enqueueArtifactFinalization } from '@/features/sync/artifact-write';
import { buildProjectDocPath, type ProjectScope } from '../sync/project-scope';
import type { FileNode, FilePolicy, FilePolicyAction } from './types';

type FileAccessAction = Extract<FilePolicyAction, 'access' | 'preview' | 'download'>;
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

export interface FileAccessMaterial {
    url: string;
    expiresAt: number;
    file?: FileNode;
    contentType?: string;
}

export interface FileUploadSession {
    uploadId: string;
    fileId: string;
    storagePath: string;
    versionNumber: number;
}

export interface FileUploadProgress {
    bytesTransferred: number;
    totalBytes: number;
    percent: number;
}

export interface RepositoryFileVersionUploadResult {
    storagePath: string;
    size: number;
    mimeType: string;
}

interface ListFilesOptions {
    parentId?: string;
}

export interface FileAccessOptions {
    reason?: string;
    storageRef?: string;
}

export type FileMetadataChanges = Partial<Pick<
    FileNode,
    'name' | 'parentId' | 'versionNumber' | 'previousVersionId' | 'storageRef' | 'storagePath' | 'mimeType' | 'size'
>>;

function stripLegacyUrls(file: FileNode): FileNode {
    const {
        url: _url,
        downloadUrl: _downloadUrl,
        ...sanitizedFile
    } = file as FileNode & { downloadUrl?: unknown };

    return sanitizedFile as FileNode;
}

async function callFiles<TRequest, TResponse>(
    command: BackendCommand<TRequest, TResponse>,
    input: TRequest
): Promise<TResponse> {
    return callBackendCommand(command, input);
}

function scopedPayload(scope: ProjectScope): ProjectScope;
function scopedPayload<TExtra extends Record<string, unknown>>(scope: ProjectScope, extra: TExtra): ProjectScope & TExtra;
function scopedPayload<TExtra extends Record<string, unknown>>(scope: ProjectScope, extra?: TExtra): ProjectScope & TExtra {
    return {
        orgId: scope.orgId,
        projectId: scope.projectId,
        ...(extra ?? {}),
    } as ProjectScope & TExtra;
}

function requireString(value: unknown, fieldName: string): string {
    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`Files service response missing ${fieldName}`);
    }
    return value;
}

function requireNumber(value: unknown, fieldName: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`Files service response missing ${fieldName}`);
    }
    return value;
}

function mapFiles(files: FileNode[] | undefined): FileNode[] {
    return (files ?? []).map(stripLegacyUrls);
}

function toUploadProgress(bytesTransferred: number, totalBytes: number): FileUploadProgress {
    return {
        bytesTransferred,
        totalBytes,
        percent: totalBytes === 0 ? 0 : (bytesTransferred / totalBytes) * 100,
    };
}

async function waitForUpload(
    task: UploadTask,
    onProgress?: (progress: FileUploadProgress) => void
): Promise<void> {
    const unsubscribe = task.onProgress(({ bytesTransferred, totalBytes }) => {
        onProgress?.(toUploadProgress(bytesTransferred, totalBytes));
    });

    try {
        await task.finished;
    } finally {
        unsubscribe();
    }
}

function validateFileSize(file: File): string | null {
    if (file.size > MAX_FILE_SIZE) {
        return `File size exceeds 200MB limit. This file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`;
    }
    return null;
}

export async function listFiles(
    scope: ProjectScope,
    options: ListFilesOptions = {}
): Promise<FileNode[]> {
    const response = await callFiles(firebaseFunctionCommands.listProjectFiles, scopedPayload(scope, {
        parentId: options.parentId,
    }));

    return mapFiles(response.files);
}

export async function searchFiles(scope: ProjectScope, query: string): Promise<FileNode[]> {
    const response = await callFiles(firebaseFunctionCommands.searchProjectFiles, scopedPayload(scope, {
        query,
    }));

    return mapFiles(response.files);
}

export async function listTrashedFiles(scope: ProjectScope): Promise<FileNode[]> {
    const response = await callFiles(firebaseFunctionCommands.listProjectTrashedFiles, scopedPayload(scope));

    return mapFiles(response.files);
}

export async function requestFileAccess(
    scope: ProjectScope,
    fileId: string,
    action: FileAccessAction,
    options?: string | FileAccessOptions
): Promise<FileAccessMaterial> {
    const accessOptions = typeof options === 'string' ? { reason: options } : options;
    const response = await getBlobStoreClient().getAccessUrl({
        scope,
        fileId,
        action,
        ...(accessOptions?.reason ? { reason: accessOptions.reason } : {}),
        ...(accessOptions?.storageRef ? { storageRef: accessOptions.storageRef } : {}),
    });

    const access: FileAccessMaterial = {
        url: requireString(response.url, 'url'),
        expiresAt: requireNumber(response.expiresAt, 'expiresAt'),
    };

    if (response.file) {
        access.file = stripLegacyUrls(response.file);
    }
    if (typeof response.contentType === 'string') {
        access.contentType = response.contentType;
    }

    return access;
}

export async function createUploadSession(scope: ProjectScope, file: File): Promise<FileUploadSession> {
    const response = await getBlobStoreClient().createUploadSession({
        scope,
        fileName: file.name,
        contentType: file.type,
        byteSize: file.size,
    });

    return {
        uploadId: requireString(response.id, 'uploadId'),
        fileId: requireString(response.fileId, 'fileId'),
        storagePath: requireString(response.storagePath, 'storagePath'),
        versionNumber: requireNumber(response.versionNumber, 'versionNumber'),
    };
}

export async function uploadToSession(
    session: FileUploadSession,
    file: File,
    onProgress?: (progress: FileUploadProgress) => void
): Promise<void> {
    const target: UploadSession = {
        id: session.uploadId,
        fileId: session.fileId,
        storagePath: session.storagePath,
        versionNumber: session.versionNumber,
        contentType: file.type,
    };

    await waitForUpload(getBlobStoreClient().upload(target, file), onProgress);
}

export async function finalizeUpload(
    scope: ProjectScope,
    input: {
        file: File;
        session: FileUploadSession;
        parentId: string;
        policy: FilePolicy;
    }
): Promise<FileNode> {
    const fileRecord = await getBlobStoreClient().finalizeUpload({
        scope,
        fileId: input.session.fileId,
        sessionId: input.session.uploadId,
        storagePath: input.session.storagePath,
        versionNumber: input.session.versionNumber,
        parentId: input.parentId,
        fileName: input.file.name,
        contentType: input.file.type,
        byteSize: input.file.size,
        policy: input.policy,
    });

    return stripLegacyUrls(fileRecord);
}

export async function uploadRepositoryFileVersion(
    file: File,
    scope: ProjectScope,
    fileId: string,
    versionNumber: number,
    onProgress?: (progress: FileUploadProgress) => void
): Promise<RepositoryFileVersionUploadResult> {
    const userId = getEffectiveUserId();
    if (!userId) throw new Error('User must be signed in to upload files.');

    const sizeError = validateFileSize(file);
    if (sizeError) throw new Error(sizeError);

    const storagePath = `${buildProjectDocPath(scope)}/files/${fileId}/v${versionNumber}/${file.name}`;
    const target: UploadSession = {
        id: `file-version-${fileId}-v${versionNumber}`,
        fileId,
        storagePath,
        versionNumber,
        contentType: file.type,
        customMetadata: { uploadedBy: userId },
    };

    await waitForUpload(getBlobStoreClient().upload(target, file), onProgress);
    await enqueueArtifactFinalization({
        scope,
        actorId: userId,
        feature: 'files',
        capability: 'CREATE_FILES',
        conflictUnit: `files.artifacts/${fileId}`,
        artifactType: 'fileVersion',
        entityId: fileId,
        storagePath,
        contentType: file.type,
        metadata: {
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            versionNumber,
        },
    });

    return {
        storagePath,
        size: file.size,
        mimeType: file.type,
    };
}

export async function updateFileMetadata(
    scope: ProjectScope,
    fileId: string,
    changes: FileMetadataChanges
): Promise<void> {
    await callFiles(firebaseFunctionCommands.updateProjectFileMetadata, scopedPayload(scope, {
        fileId,
        changes,
    }));
}

export async function updateFilePolicy(
    scope: ProjectScope,
    fileId: string,
    policy: FilePolicy
): Promise<void> {
    await callFiles(firebaseFunctionCommands.updateProjectFilePolicy, scopedPayload(scope, {
        fileId,
        policy,
    }));
}

export async function moveFileToTrash(scope: ProjectScope, fileId: string): Promise<void> {
    await callFiles(firebaseFunctionCommands.moveProjectFileToTrash, scopedPayload(scope, {
        fileId,
    }));
}

export async function restoreFileFromTrash(scope: ProjectScope, fileId: string): Promise<void> {
    await callFiles(firebaseFunctionCommands.restoreProjectFileFromTrash, scopedPayload(scope, {
        fileId,
    }));
}
